import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Building2, Search, Clock, FileDown, Trash2, Plus, PenTool, RefreshCw, FileCheck2, ArrowLeft, ArrowRight, FileSpreadsheet, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RATE_COLS = [
  ['peak1_rate', 'Peak 1 $/kWh'], ['peak1_block', 'Peak 1 Block'], ['peak_balance_rate', 'Peak Bal $/kWh'],
  ['shoulder_rate', 'Shoulder $/kWh'], ['offpeak_rate', 'Off Peak $/kWh'], ['cl1_rate', 'CL 1 $/kWh'],
  ['cl2_rate', 'CL 2 $/kWh'], ['demand1_rate', 'Demand 1'], ['demand2_rate', 'Demand 2'],
  ['service_charge_daily', 'Service $/day'], ['solar_rate', 'Solar $/kWh'], ['metering_monthly', 'Metering $/mth'],
];
const CUST_FIELDS = [
  ['company_name', 'Company Name'], ['trading_name', 'Trading Name'], ['abn', 'ABN'], ['acn', 'ACN'],
  ['contact_name', 'Contact Name'], ['phone', 'Phone'], ['mobile', 'Mobile'], ['accounts_email', 'Accounts Email'],
  ['billing_address', 'Billing Address'], ['billing_suburb', 'Suburb'], ['billing_state', 'State'], ['billing_postcode', 'Postcode'],
  ['representative_name', 'Representative'], ['product_name', 'Product Name'],
  ['proposed_transfer_date', 'Proposed Transfer Date'],
  ['supply_period_months', 'Supply Period (Months)'], ['quote_valid_until', 'Quote Valid Until'],
  ['customer_sign_name', 'Signer Name'],
  ['ap_contact_name', 'Accounts Payable Name'], ['ap_email', 'Accounts Payable Email'], ['ap_phone', 'Accounts Payable Phone'],
];

const emptyForm = () => Object.fromEntries(CUST_FIELDS.map(([k]) => [k, '']).concat([['customer_type', ''], ['connection_type', ''], ['pricing_type', ''], ['special_instructions', '']]));
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const ES_LABELS = { processing: 'Processing', sent: 'Awaiting signature', delivered: 'Viewed', completed: 'Signed ✓', declined: 'Declined', voided: 'Voided' };

export default function SMEMultisite() {
  const { user, token } = useAuth();
  const [tab, setTab] = useState('new');
  const [step, setStep] = useState(1);
  const [nmiText, setNmiText] = useState('');
  const [looking, setLooking] = useState(false);
  const [sites, setSites] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [entities, setEntities] = useState([]);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [esContract, setEsContract] = useState(null);
  const [esName, setEsName] = useState('');
  const [esEmail, setEsEmail] = useState('');
  const [esSending, setEsSending] = useState(false);
  const [esRefreshing, setEsRefreshing] = useState('');

  useEffect(() => {
    axios.get(API_URL + '/api/tariffs?authorization=' + token).then((r) => setTariffs(r.data || [])).catch(() => {});
    fetchHistory();
  }, [token]);

  const fetchHistory = () => {
    axios.get(API_URL + '/api/multisite/contracts?authorization=' + token).then((r) => setHistory(r.data || [])).catch(() => {});
  };

  const lookup = () => {
    const nmis = nmiText.split(/[\n,;\s]+/).map((n) => n.trim()).filter(Boolean);
    if (!nmis.length) { toast.error('Paste at least one NMI'); return; }
    setLooking(true);
    axios.post(API_URL + '/api/multisite/lookup?authorization=' + token, { nmis })
      .then((r) => {
        if (!r.data.msats_enabled) toast.warning('MSATS lookup API not configured (Settings → NMI Lookup) — using NMI prefix detection. Select tariffs manually.');
        setSites(r.data.sites.map((s) => ({
          ...s,
          tariff_id: s.matched_tariff_id || 'none',
          discount: '',
          product: '',
          rates: s.rates || Object.fromEntries(RATE_COLS.map(([k]) => [k, ''])),
        })));
        toast.success(r.data.sites.length + ' NMI(s) looked up');
        setStep(2);
      })
      .catch((e) => toast.error(e.response?.data?.detail || 'Lookup failed'))
      .finally(() => setLooking(false));
  };

  const updateSite = (i, patch) => setSites((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const selectTariff = (i, tariffId) => {
    const t = tariffs.find((x) => x.id === tariffId);
    updateSite(i, {
      tariff_id: tariffId,
      tariff_code: t ? t.tariff_code : sites[i].tariff_code,
      rates: t ? {
        peak1_rate: t.usage_rate_peak ?? t.usage_rate_flat ?? '', peak1_block: '', peak_balance_rate: '',
        shoulder_rate: t.usage_rate_shoulder ?? '', offpeak_rate: t.usage_rate_off_peak ?? '',
        cl1_rate: t.controlled_load_rate ?? t.usage_rate_controlled_load ?? '', cl2_rate: '',
        demand1_rate: t.demand_rate ?? '', demand2_rate: '',
        service_charge_daily: t.supply_charge_daily ?? '', solar_rate: t.solar_fit ?? t.feed_in_tariff ?? '', metering_monthly: '',
      } : sites[i].rates,
    });
  };

  const siteTariffs = (site) => tariffs.filter((t) => !site.state || (t.state || '').toUpperCase() === site.state.toUpperCase());

  const buildPayload = () => ({
    ...form,
    entities: entities.filter((e) => e.entity_abn || e.entity_name),
    sites: sites.map((s) => ({
      client_name: form.company_name, abn: form.abn, nmi: s.nmi,
      tariff: s.tariff_code || '', meter_type: s.meter_type || '', address: s.address || '',
      suburb: s.suburb || '', state: s.state || '', postcode: s.postcode || '',
      product: s.product || form.product_name || '', discount: s.discount || '',
      ...s.rates,
    })),
  });

  const saveAndGenerate = () => {
    if (!form.company_name) { toast.error('Enter the company name'); return; }
    setSaving(true);
    axios.post(API_URL + '/api/multisite/contracts?authorization=' + token, buildPayload())
      .then((r) => {
        toast.success('Multisite contract ' + r.data.agreement_number + ' saved');
        downloadPdf(r.data);
        fetchHistory();
        setTab('history');
        setStep(1); setSites([]); setNmiText(''); setForm(emptyForm()); setEntities([]);
      })
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to save contract'))
      .finally(() => setSaving(false));
  };

  const downloadPdf = (c) => {
    axios.get(API_URL + '/api/multisite/contracts/' + c.id + '/pdf?authorization=' + token, { responseType: 'blob' })
      .then((r) => {
        const url = URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'NBE_Multisite_' + (c.agreement_number || c.id.slice(0, 8)) + '.pdf';
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error('PDF generation failed'));
  };

  const downloadCaf = (c) => {
    axios.get(API_URL + '/api/multisite/contracts/' + c.id + '/caf?authorization=' + token, { responseType: 'blob' })
      .then((r) => {
        const url = URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'CAF - ' + (c.company_name || 'Customer') + '.xlsx';
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error('CAF generation failed'));
  };

  const emailCaf = (c) => {
    toast.info('Emailing CAF to contracts@nextbusinessenergy.com.au...');
    axios.post(API_URL + '/api/multisite/contracts/' + c.id + '/send-caf?authorization=' + token)
      .then((r) => { toast.success(r.data.message); fetchHistory(); })
      .catch((e) => toast.error(e.response?.data?.detail || 'CAF email failed'));
  };

  const sendESign = () => {
    if (!esEmail || !esName) { toast.error('Enter signer name and email'); return; }
    setEsSending(true);
    axios.post(API_URL + '/api/contracts/' + esContract.id + '/esign/send?authorization=' + token, {
      signer_email: esEmail, signer_name: esName, base_url: window.location.origin,
    })
      .then((r) => { toast.success('Sent via ' + (r.data.provider === 'pandadoc' ? 'PandaDoc' : 'DocuSign')); setEsContract(null); fetchHistory(); })
      .catch((e) => toast.error(e.response?.data?.detail || 'Send failed'))
      .finally(() => setEsSending(false));
  };

  const refreshESign = (c) => {
    setEsRefreshing(c.id);
    axios.get(API_URL + '/api/contracts/' + c.id + '/esign/status?authorization=' + token)
      .then((r) => { toast.success(r.data.status_label || r.data.status); fetchHistory(); })
      .catch((e) => toast.error(e.response?.data?.detail || 'Status check failed'))
      .finally(() => setEsRefreshing(''));
  };

  const downloadSigned = (c) => {
    axios.get(API_URL + '/api/contracts/' + c.id + '/esign/signed-document?authorization=' + token, { responseType: 'blob' })
      .then((r) => {
        const url = URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'NBE_Multisite_' + (c.agreement_number || '') + '_SIGNED.pdf';
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error('Signed document not available yet'));
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="sme-multisite-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Building2 className="w-6 h-6 text-[#8CC63F]" /> SME Multisite</h1>
          <p className="text-slate-500 mt-1">Generate multisite customer agreements with an automatic site schedule</p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="new" data-testid="ms-tab-new">New Contract</TabsTrigger>
            <TabsTrigger value="history" data-testid="ms-tab-history">History ({history.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="new" className="space-y-5 mt-4">
            {step === 1 && (
              <Card>
                <CardHeader><CardTitle>Step 1 — Paste NMIs</CardTitle><CardDescription>One per line (or comma separated). Each NMI is looked up via your MSATS connection to identify the tariff, address and meter type.</CardDescription></CardHeader>
                <CardContent className="space-y-3">
                  <Textarea rows={8} value={nmiText} onChange={(e) => setNmiText(e.target.value)} placeholder={'4102345678\n6203456789\n3104567890'} className="font-mono" data-testid="ms-nmi-input" />
                  <Button onClick={lookup} disabled={looking} className="bg-[#015492] hover:bg-[#013f6e]" data-testid="ms-lookup-btn">
                    {looking ? <Clock className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}Lookup NMIs
                  </Button>
                </CardContent>
              </Card>
            )}

            {step === 2 && (
              <Card>
                <CardHeader><CardTitle>Step 2 — Customer Information</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-3 gap-3">
                    {CUST_FIELDS.map(([k, label]) => (
                      <div key={k}>
                        <Label className="text-xs">{label}</Label>
                        <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`ms-field-${k}`} />
                      </div>
                    ))}
                    <div>
                      <Label className="text-xs">Customer Type</Label>
                      <Select value={form.customer_type} onValueChange={(v) => setForm({ ...form, customer_type: v })}>
                        <SelectTrigger data-testid="ms-customer-type"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{['Sole Trader', 'Partnership', 'Company', 'Business', 'Trust'].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Connection Type</Label>
                      <Select value={form.connection_type} onValueChange={(v) => setForm({ ...form, connection_type: v })}>
                        <SelectTrigger data-testid="ms-connection-type"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{['Transfer', 'Move in', 'Renewal'].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Pricing Type</Label>
                      <Select value={form.pricing_type} onValueChange={(v) => setForm({ ...form, pricing_type: v })}>
                        <SelectTrigger data-testid="ms-pricing-type"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{['Unbundled Pricing', 'Bundled Pricing'].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label className="font-semibold">Special Instructions — Additional Contract Terms</Label>
                    <Textarea rows={4} value={form.special_instructions} onChange={(e) => setForm({ ...form, special_instructions: e.target.value })} placeholder="Any additional contract terms — these appear in the Special Instructions box at the bottom of the agreement" data-testid="ms-special-instructions" />
                  </div>


                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="font-semibold">Schedule 2 — Applicable Entities (optional)</Label>
                      <Button variant="outline" size="sm" onClick={() => setEntities([...entities, { entity_abn: '', entity_name: '' }])} data-testid="ms-add-entity"><Plus className="w-3 h-3 mr-1" />Add Entity</Button>
                    </div>
                    {entities.map((e, i) => (
                      <div key={i} className="flex gap-2 mb-2">
                        <Input placeholder="Entity ABN" value={e.entity_abn} onChange={(ev) => setEntities(entities.map((x, xi) => (xi === i ? { ...x, entity_abn: ev.target.value } : x)))} className="w-56" />
                        <Input placeholder="Entity Name" value={e.entity_name} onChange={(ev) => setEntities(entities.map((x, xi) => (xi === i ? { ...x, entity_name: ev.target.value } : x)))} />
                        <button onClick={() => setEntities(entities.filter((_, xi) => xi !== i))} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
                    <Button onClick={() => setStep(3)} className="flex-1 bg-[#015492] hover:bg-[#013f6e]" data-testid="ms-to-step3">Site Schedule<ArrowRight className="w-4 h-4 ml-2" /></Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {step === 3 && (
              <Card>
                <CardHeader><CardTitle>Step 3 — Site Schedule ({sites.length} sites)</CardTitle><CardDescription>Tariffs from MSATS lookup, rates pulled from your uploaded SME rate sheets. Adjust and set a discount per site.</CardDescription></CardHeader>
                <CardContent className="space-y-4">
                  {sites.map((s, i) => (
                    <div key={s.nmi} className="border rounded-lg p-4 space-y-3" data-testid={`ms-site-${s.nmi}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">{s.nmi}</span>
                        {s.state && <Badge variant="outline">{s.state}</Badge>}
                        {s.distributor && <Badge variant="outline">{s.distributor}</Badge>}
                        <Badge className={s.lookup_method === 'msats' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>{s.lookup_method === 'msats' ? 'MSATS' : 'Prefix detected'}</Badge>
                      </div>
                      <div className="grid md:grid-cols-4 gap-2">
                        <div className="md:col-span-2">
                          <Label className="text-xs">Tariff (from rate sheets)</Label>
                          <Select value={s.tariff_id} onValueChange={(v) => selectTariff(i, v)}>
                            <SelectTrigger data-testid={`ms-tariff-${s.nmi}`}><SelectValue placeholder="Select tariff" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">{s.tariff_code ? s.tariff_code + ' (not in rate sheets)' : 'Select tariff'}</SelectItem>
                              {siteTariffs(s).map((t) => <SelectItem key={t.id} value={t.id}>{t.tariff_code} — {t.tariff_name} ({t.network_distributor})</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Discount</Label>
                          <Input value={s.discount} onChange={(e) => updateSite(i, { discount: e.target.value })} placeholder="e.g. 10%" data-testid={`ms-discount-${s.nmi}`} />
                        </div>
                        <div>
                          <Label className="text-xs">Meter Type</Label>
                          <Input value={s.meter_type} onChange={(e) => updateSite(i, { meter_type: e.target.value })} />
                        </div>
                        <div className="md:col-span-2">
                          <Label className="text-xs">Address</Label>
                          <Input value={s.address} onChange={(e) => updateSite(i, { address: e.target.value })} />
                        </div>
                        <div>
                          <Label className="text-xs">Suburb</Label>
                          <Input value={s.suburb} onChange={(e) => updateSite(i, { suburb: e.target.value })} />
                        </div>
                        <div>
                          <Label className="text-xs">Postcode</Label>
                          <Input value={s.postcode} onChange={(e) => updateSite(i, { postcode: e.target.value })} />
                        </div>
                      </div>
                      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                        {RATE_COLS.map(([k, label]) => (
                          <div key={k}>
                            <Label className="text-[10px] text-slate-500">{label}</Label>
                            <Input value={s.rates[k] ?? ''} onChange={(e) => updateSite(i, { rates: { ...s.rates, [k]: e.target.value } })} className="h-8 text-xs" />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => setStep(2)}><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
                    <Button onClick={saveAndGenerate} disabled={saving} className="flex-1 bg-[#8CC63F] hover:bg-[#7ab332] text-white" data-testid="ms-generate-btn">
                      {saving ? <Clock className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}Save & Generate Multisite Contract PDF
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="history" className="space-y-3 mt-4" data-testid="ms-history-list">
            {history.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-slate-500">No multisite contracts yet</CardContent></Card>
            ) : history.map((c) => (
              <Card key={c.id} className="border-l-4 border-l-[#8CC63F]" data-testid={`ms-history-row-${c.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="space-y-1 flex-1 min-w-[240px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold">{c.company_name || 'Untitled'}</h3>
                        <Badge variant="outline">{c.agreement_number}</Badge>
                        <Badge className="bg-blue-100 text-blue-800">{(c.sites || []).length} sites</Badge>
                        {c.esign_status && (
                          <Badge className={c.esign_status === 'completed' ? 'bg-green-600 text-white' : c.esign_status === 'declined' || c.esign_status === 'voided' ? 'bg-red-100 text-red-800' : 'bg-indigo-100 text-indigo-800'}>
                            {c.esign_provider === 'pandadoc' ? 'PandaDoc' : 'DocuSign'}: {ES_LABELS[c.esign_status] || c.esign_status}
                          </Badge>
                        )}
                        {c.caf_sent_at && <Badge className="bg-teal-100 text-teal-800">CAF sent {fmtDate(c.caf_sent_at)}</Badge>}
                      </div>
                      <p className="text-xs text-slate-500">{c.contact_name && c.contact_name + ' | '}{c.accounts_email && c.accounts_email + ' | '}Rep: {c.created_by_name} | Created {fmtDate(c.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => downloadPdf(c)} title="Download PDF" data-testid={`ms-pdf-${c.id}`}><FileDown className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" className="text-teal-600" onClick={() => downloadCaf(c)} title="Download CAF (Excel)" data-testid={`ms-caf-${c.id}`}><FileSpreadsheet className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" className="text-teal-600" onClick={() => emailCaf(c)} title="Email CAF + contract to contracts@nextbusinessenergy.com.au" data-testid={`ms-caf-email-${c.id}`}><Send className="w-4 h-4" /></Button>
                      {c.esign_document_id || c.docusign_envelope_id ? (
                        c.esign_status === 'completed' ? (
                          <Button variant="ghost" size="sm" className="text-green-600" onClick={() => downloadSigned(c)} title="Download signed contract" data-testid={`ms-signed-${c.id}`}><FileCheck2 className="w-4 h-4" /></Button>
                        ) : (
                          <Button variant="ghost" size="sm" className="text-indigo-600" onClick={() => refreshESign(c)} disabled={esRefreshing === c.id} title="Refresh signature status" data-testid={`ms-refresh-${c.id}`}><RefreshCw className={'w-4 h-4' + (esRefreshing === c.id ? ' animate-spin' : '')} /></Button>
                        )
                      ) : (
                        <Button variant="ghost" size="sm" className="text-indigo-600" onClick={() => { setEsContract(c); setEsName(c.customer_sign_name || c.contact_name || ''); setEsEmail(c.accounts_email || ''); }} title="Send for eSignature" data-testid={`ms-esign-${c.id}`}><PenTool className="w-4 h-4" /></Button>
                      )}
                      {user?.role === 'admin' && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-red-500" data-testid={`ms-delete-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete multisite contract?</AlertDialogTitle>
                              <AlertDialogDescription>{c.agreement_number} for "{c.company_name}" will be permanently deleted.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => axios.delete(API_URL + '/api/multisite/contracts/' + c.id + '?authorization=' + token).then(() => { toast.success('Deleted'); fetchHistory(); })}>Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>
        </Tabs>

        <Dialog open={!!esContract} onOpenChange={(o) => !o && setEsContract(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Send for eSignature — {esContract?.agreement_number}</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-500">Sent via the active provider (DocuSign/PandaDoc — Settings → eSignature). Once signed, the signed contract is stored here automatically.</p>
            <div className="space-y-3">
              <div><Label>Signer Name</Label><Input value={esName} onChange={(e) => setEsName(e.target.value)} data-testid="ms-signer-name" /></div>
              <div><Label>Signer Email</Label><Input value={esEmail} onChange={(e) => setEsEmail(e.target.value)} data-testid="ms-signer-email" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEsContract(null)}>Cancel</Button>
              <Button onClick={sendESign} disabled={esSending} className="bg-indigo-600 hover:bg-indigo-700" data-testid="ms-esign-send-btn">{esSending ? 'Sending...' : 'Send for Signature'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
